import React from "react";
import PropTypes from "prop-types";
import {satisfaction} from "configuration/constants";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";

import BaseFieldComponent from "../BaseFieldComponent";
import SelectChipContainer from "../../../../connectui/component/input/SelectChipContainer";
import Text from "connectui/component/display/Text";
import FlexContainer from "connectui/component/layout/FlexContainer";
import DropDownSelect from "connectui/component/input/dropdown/DropDownSelect";
import ChipDropDownBase from "connectui/component/input/dropdown/base/ChipDropDownBase";
import SvgIcon from "connectui/component/display/SvgIcon";

const sentimentKeys = Object.keys(satisfaction);
const sentimentValues = Object.values(satisfaction);
const options = sentimentValues.map(({label}) => label);
const filterOptions = options.filter((opt) => opt !== "Unknown");

const icons = {
  unsatisfied: ICONS["unsatisfied"],
  somewhat_satisfied: ICONS["somewhat-satisfied"],
  neutral: ICONS["neutral"],
  satisfied: ICONS["satisfied"],
  highly_satisfied: ICONS["highly-satisfied"],
};

const colors = {
  highly_satisfied: "success",
  satisfied: "bluelight",
  neutral: "purple",
  somewhat_satisfied: "warning",
  unsatisfied: "error",
  unknown: "bluegray",
};

const getKey = (option, prop = "label") =>
  sentimentKeys.find((k) => satisfaction[k][prop] === option);

const getColoredChip = (isSelected, baseColor, theme) => ({
  backgroundColor: isSelected
    ? theme.colors[`${baseColor}50`]
    : theme.colors.gray100,
  color: isSelected ? theme.colors[`${baseColor}700`] : theme.colors.gray700,
  "&:hover": {
    backgroundColor: isSelected
      ? theme.colors[`${baseColor}100`]
      : theme.colors.gray200,
  },
});

function ColoredChip({sentimentKey, isSelected, small, option}) {
  const {theme} = useTheme();

  return (
    <FlexContainer alignItems="center" gap={theme.spacing(1)}>
      {!!icons[sentimentKey] && (
        <SvgIcon
          component={icons[sentimentKey]}
          sx={{
            width: small ? "12px" : "16px",
            height: small ? "12px" : "16px",
            display: "flex",
            color: isSelected
              ? theme.colors[`${colors[sentimentKey]}600`]
              : theme.colors.gray700,
          }}
        />
      )}
      <Text
        variant={small ? "textXS" : "textSM"}
        weight="medium"
        color={isSelected ? `${colors[sentimentKey]}600` : "gray700"}
        sx={{
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        }}
      >
        {option}
      </Text>
    </FlexContainer>
  );
}

const handleFilterChange = (fieldData, handleValueChange) => (newValue) => {
  let newSelection = newValue.map(
    (v) => sentimentValues.find((s_v) => s_v.label === v).value,
  );
  handleValueChange(
    "custom",
    !!newValue.length ? [{path: fieldData.field, value: newSelection}] : [],
  );
};

function renderFilter({theme, fieldData, handleValueChange}) {
  const selectedOpts = fieldData.val.map(
    (v) => satisfaction[getKey(v, "value")].label,
  );

  return (
    <SelectChipContainer
      options={filterOptions}
      multiSelect
      selectedOptions={selectedOpts}
      onChange={handleFilterChange(fieldData, handleValueChange)}
      sx={{
        getChipStyles: ({option, isSelected}) => {
          const key = getKey(option);
          return getColoredChip(isSelected, colors[key], theme);
        },
      }}
      customRender={(option, isSelected) => {
        const key = getKey(option);
        return (
          <ColoredChip
            sentimentKey={key}
            isSelected={isSelected}
            option={option}
          />
        );
      }}
    />
  );
}

const renderInput = ({theme, variant, fieldData, handleValueChange}) => {
  if (variant === "dropdown") {
    const selectedKey = getKey(fieldData.val, "value");

    return (
      <div>
        <DropDownSelect
          dropDownBase={ChipDropDownBase}
          selected={selectedKey}
          label={
            <ColoredChip
              small
              isSelected
              sentimentKey={selectedKey}
              option={satisfaction[selectedKey]?.label}
            />
          }
          sx={{
            label: {
              minWidth: 0,
              backgroundColor: theme.colors[`${colors[selectedKey]}50`],
              "&:hover": {
                backgroundColor: theme.colors[`${colors[selectedKey]}100`],
              },
            },
          }}
          onChange={handleValueChange}
        >
          {options.map((option) => {
            const key = getKey(option);

            return (
              <FlexContainer
                key={key}
                value={key}
                alignItems="center"
                gap={theme.spacing(1.5)}
              >
                {!!icons[key] && (
                  <SvgIcon
                    component={icons[key]}
                    sx={{
                      width: "16px",
                      height: "16px",
                      color: theme.colors[`${colors[key]}600`],
                      display: "flex",
                    }}
                  />
                )}
                <Text
                  variant="textSM"
                  weight="medium"
                  color={`${colors[key]}700`}
                >
                  {option}
                </Text>
              </FlexContainer>
            );
          })}
        </DropDownSelect>
      </div>
    );
  }

  return (
    <SelectChipContainer
      options={options}
      multiSelect={true}
      selectedOptions={
        Array.isArray(fieldData.val)
          ? fieldData.val.map(
              (v) => sentimentValues.find((s) => s.value === v)?.label,
            )
          : []
      }
      sx={{
        getChipStyles: ({option, isSelected}) => {
          const key = getKey(option);
          return getColoredChip(isSelected, colors[key], theme);
        },
      }}
      onChange={(newSelection) => {
        const newValues = newSelection.map(
          (label) => sentimentValues.find((s) => s.label === label)?.value,
        );
        handleValueChange(fieldData.type, newValues);
      }}
      customRender={(option, isSelected) => {
        const key = getKey(option);
        return (
          <ColoredChip
            sentimentKey={key}
            isSelected={isSelected}
            option={option}
          />
        );
      }}
    />
  );
};

const SentimentField = ({fieldData, handleValueChange, variant, ...props}) => {
  const {theme} = useTheme();

  const getOperators = () => [
    "contains",
    "not_contains",
    "exists",
    "not_exists",
    "is_one_of",
  ];

  const getLabel = () => {
    let value = fieldData?.val;
    if (!value) {
      return satisfaction.unknown.label;
    }
    if (value instanceof Array) {
      return value
        .map((v) =>
          sentimentValues
            .find((s) => s.value === v)
            ?.label.toLowerCase()
            .replace(/_/g, " "),
        )
        .join(", ");
    }

    return sentimentValues.find((s) => s.value === value)?.label || value;
  };

  return (
    <BaseFieldComponent
      renderInput={() =>
        renderInput({theme, variant, fieldData, handleValueChange})
      }
      renderFilter={() => renderFilter({theme, fieldData, handleValueChange})}
      getLabel={getLabel}
      getOperators={getOperators}
      {...props}
    />
  );
};

SentimentField.propTypes = {
  fieldData: PropTypes.object.isRequired,
  handleValueChange: PropTypes.func.isRequired,
};

export default SentimentField;
